import { useEffect, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CurrencySelectorCurrencyFragment,
  MileageRateConfigurationFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import Files from 'modules/expenses/components/expenses/Detail/_newForm/Files'
import AdditionalInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/AdditionalInformation'
import BudgetAndProject from 'modules/expenses/components/expenses/Detail/_newForm/Sections/BudgetAndProject'
import CostCenterInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/CostCenters'
import ExpenseInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/ExpenseInformation'
import MileageInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/MileageInformation'
import TripInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/TripInformation'
import F0NewMileageForm from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/f0'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { Expensable } from 'modules/expenses/types/Expensable'
import { BudgetOption } from 'modules/finance/hooks/useBudgets'

interface NewMileageFormProps {
  customizationFields: CustomizationField[]
  expensable?: Expensable
  budgetOptions: BudgetOption[]
  projectWorkers: ProjectWorker[]
  hasProjectsFeature: boolean
  loadingProjectWorkers: boolean
  loadingBudgetOptions: boolean
  canEditExpensesWithPendingStatus: boolean
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  onEmployeeIdChange: (employeeId: number | undefined) => void
  onEffectiveOnChange: (value: string) => void
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
  canSeeEmployeeLegalEntity: boolean
  rates?: MileageRateConfigurationFragment[]
  currencies?: CurrencySelectorCurrencyFragment[]
  scrollTo?: string | undefined
  hasMileageMultipleStops?: boolean
  hasExpensablesF0Forms?: boolean
}

const LegacyNewMileageForm = (props: NewMileageFormProps) => {
  const { section } = useOutletContext<RouteContextParams>()
  const {
    expensable,
    budgetOptions,
    projectWorkers,
    hasProjectsFeature,
    loadingProjectWorkers,
    loadingBudgetOptions,
    registerDropHandler,
    onEmployeeIdChange,
    onEffectiveOnChange,
    rates,
    scrollTo,
    canEditExpensesWithPendingStatus,
  } = props

  const asReviewer = !isMyExpensesSection(section)

  const { employee } = useCurrentSession()

  // State to track distance calculation loading
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false)

  const tripInformationRef = useRef<HTMLDivElement>(null)
  const mileageInformationRef = useRef<HTMLDivElement>(null)
  const basicInformationRef = useRef<HTMLDivElement>(null)
  const budgetAndProjectRef = useRef<HTMLDivElement>(null)
  const additionalInformationRef = useRef<HTMLDivElement>(null)
  const costCenterInformationRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // The timeout gives a more graceful animation experience
    setTimeout(() => {
      let ref: HTMLDivElement | null = null
      switch (scrollTo) {
        case 'basicInformation':
          ref = basicInformationRef.current
          break
        case 'tripInformation':
          ref = tripInformationRef.current
          break
        case 'mileageInformation':
          ref = mileageInformationRef.current
          break
        case 'budgetAndProject':
          ref = budgetAndProjectRef.current
          break
        case 'additionalInformation':
          ref = additionalInformationRef.current
          break
      }
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 300)
  }, [scrollTo])

  return (
    <Box gap="s24" paddingY={expensable ? 's24' : 's8'}>
      <BasicDetails
        expensable={expensable}
        canEditExpensesWithPendingStatus={canEditExpensesWithPendingStatus}
      />
      <Box ref={basicInformationRef}>
        <ExpenseInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          onEmployeeIdChange={onEmployeeIdChange}
          isAutomaticExpensable={false}
          documentType={'default'}
          onEffectiveOnChange={onEffectiveOnChange}
        />
      </Box>
      <Box ref={tripInformationRef}>
        <TripInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          setIsCalculatingDistance={setIsCalculatingDistance}
          hasMileageMultipleStops={props.hasMileageMultipleStops}
        />
      </Box>
      <Box ref={mileageInformationRef}>
        <MileageInformation
          {...props}
          documentType={'default'}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          rates={rates}
          isCalculatingDistance={isCalculatingDistance}
          setIsCalculatingDistance={setIsCalculatingDistance}
        />
      </Box>
      <Box ref={budgetAndProjectRef}>
        <BudgetAndProject
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          budgetOptions={budgetOptions}
          projectWorkers={projectWorkers}
          hasProjectsFeature={hasProjectsFeature}
          loadingProjectWorkers={loadingProjectWorkers}
          loadingBudgetOptions={loadingBudgetOptions}
        />
      </Box>
      <Box ref={costCenterInformationRef}>
        <CostCenterInformation
          {...props}
          currentEmployee={employee}
          resourceId={expensable?.id}
          asReviewer={asReviewer}
          resourceType={'expense'}
          documentType={'default'}
        />
      </Box>
      <Box ref={additionalInformationRef}>
        <AdditionalInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          expensable={expensable}
        />
      </Box>
      <Files
        {...props}
        currentEmployee={employee}
        fieldIdentifier="files"
        resourceType={'mileage'}
        asReviewer={asReviewer}
        documentType={'default'}
        expensable={expensable}
        registerDropHandler={registerDropHandler}
        taxAgencyOrganization={null}
      />
    </Box>
  )
}

const NewMileageForm = (props: NewMileageFormProps) => {
  if (props.hasExpensablesF0Forms) {
    return <F0NewMileageForm {...props} />
  }

  return <LegacyNewMileageForm {...props} />
}

export default NewMileageForm
